import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

const CURRENCY_MAP = {
  EUR: { symbol: '\u20AC', locale: 'it-IT' },
  USD: { symbol: '$', locale: 'en-US' },
  THB: { symbol: '\u0E3F', locale: 'th-TH' },
  GBP: { symbol: '\u00A3', locale: 'en-GB' },
  CHF: { symbol: 'Fr', locale: 'de-CH' },
  JPY: { symbol: '\u00A5', locale: 'ja-JP' },
  AUD: { symbol: 'A$', locale: 'en-AU' },
  CAD: { symbol: 'C$', locale: 'en-CA' },
  AED: { symbol: 'AED', locale: 'ar-AE' },
  SGD: { symbol: 'S$', locale: 'en-SG' },
  BRL: { symbol: 'R$', locale: 'pt-BR' },
  MXN: { symbol: 'MX$', locale: 'es-MX' },
  INR: { symbol: '\u20B9', locale: 'en-IN' },
  KRW: { symbol: '\u20A9', locale: 'ko-KR' },
  CNY: { symbol: '\u00A5', locale: 'zh-CN' },
  PLN: { symbol: 'z\u0142', locale: 'pl-PL' },
  CZK: { symbol: 'K\u010D', locale: 'cs-CZ' },
  TRY: { symbol: '\u20BA', locale: 'tr-TR' },
  SEK: { symbol: 'kr', locale: 'sv-SE' },
  NOK: { symbol: 'kr', locale: 'nb-NO' },
  DKK: { symbol: 'kr', locale: 'da-DK' },
  RON: { symbol: 'lei', locale: 'ro-RO' },
  HUF: { symbol: 'Ft', locale: 'hu-HU' },
  ILS: { symbol: '\u20AA', locale: 'he-IL' },
  ZAR: { symbol: 'R', locale: 'en-ZA' },
  ARS: { symbol: 'AR$', locale: 'es-AR' },
  COP: { symbol: 'CO$', locale: 'es-CO' },
  PEN: { symbol: 'S/', locale: 'es-PE' },
  MAD: { symbol: 'MAD', locale: 'ar-MA' },
  EGP: { symbol: 'EGP', locale: 'ar-EG' },
};

const CurrencyContext = createContext({
  currency: 'EUR',
  symbol: '€',
  fmt: (v) => `€${(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
  setCurrencyCode: () => {},
});

export const useCurrency = () => useContext(CurrencyContext);

export const CurrencyProvider = ({ children }) => {
  const { token } = useAuth();
  const [currency, setCurrency] = useState('EUR');

  useEffect(() => {
    if (token) {
      api.getUserCurrency(token).then(r => {
        if (r?.currency) setCurrency(r.currency);
      }).catch(() => {});
    }
  }, [token]);

  const info = CURRENCY_MAP[currency] || CURRENCY_MAP.EUR;

  const fmt = useCallback((v) => {
    const num = Number(v) || 0;
    return `${info.symbol}${num.toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }, [info.symbol]);

  const setCurrencyCode = useCallback(async (code) => {
    setCurrency(code);
    if (token) {
      try { await api.setUserCurrency(token, code); } catch {}
    }
  }, [token]);

  return (
    <CurrencyContext.Provider value={{ currency, symbol: info.symbol, fmt, setCurrencyCode }}>
      {children}
    </CurrencyContext.Provider>
  );
};
